import React, { useMemo } from "react";
import { Boxes, Users, ChefHat, Wine, Check } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/site/Reveal";
import { APPS, PLANS, computeCost, computeSavings, appRate } from "@/lib/pricing";
import { scrollToId } from "@/hooks/useLenis";

const ICONS = { gestionale: Boxes, team: Users, kitchen: ChefHat, bar: Wine };

export default function Pricing({ config, setConfig, onRequest }) {
  const { t } = useLang();
  const { apps, hoursPerDay, daysPerWeek, billing } = config;

  const cost = useMemo(() => computeCost(apps, hoursPerDay, daysPerWeek), [apps, hoursPerDay, daysPerWeek]);
  const savings = useMemo(() => computeSavings(apps, hoursPerDay, daysPerWeek), [apps, hoursPerDay, daysPerWeek]);
  const netBenefit = Math.round((savings.monthlySavings - cost.monthly) * 100) / 100;
  const displayCost = billing === "weekly" ? cost.weekly : cost.monthly;

  const toggleApp = (id) => {
    setConfig((c) => ({
      ...c,
      apps: c.apps.includes(id) ? c.apps.filter((a) => a !== id) : [...c.apps, id],
    }));
  };

  const applyPlan = (planApps) => {
    setConfig((c) => ({ ...c, apps: [...planApps] }));
    scrollToId("calculator");
  };

  const euro = (n) => "€" + n.toLocaleString(t.code === "de" ? "de-DE" : "it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <section id="pricing" className="relative py-24 md:py-36 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="max-w-3xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-brand mb-5">{t.pricing.overline}</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter text-white leading-[0.95]">
              {t.pricing.title}
            </h2>
            <p className="mt-6 text-white/65 text-lg font-light">{t.pricing.sub}</p>
          </div>
        </Reveal>

        <div id="calculator" className="mt-14 grid lg:grid-cols-[1.4fr_1fr] gap-6">
          {/* Controls */}
          <Reveal className="h-full">
            <div className="rounded-3xl border border-white/10 bg-ink-surface/50 p-7 md:p-9 h-full">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 mb-5">{t.pricing.selectApps}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {APPS.map((a) => {
                  const Icon = ICONS[a.id];
                  const active = apps.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      onClick={() => toggleApp(a.id)}
                      data-testid={`calc-app-${a.id}`}
                      aria-pressed={active}
                      className={`flex items-center justify-between rounded-2xl border p-4 text-left transition-colors duration-200 ${
                        active ? "border-cyan-brand bg-cyan-brand/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span className={`grid place-items-center w-10 h-10 rounded-xl ${active ? "bg-cyan-brand/20 text-cyan-brand" : "bg-white/5 text-white/60"}`}>
                          <Icon size={18} />
                        </span>
                        <span>
                          <span className="block text-white font-semibold text-sm">{t.appNames[a.id]}</span>
                          <span className="block font-mono text-xs text-white/50">{euro(a.rate)}{t.pricing.perHour}</span>
                        </span>
                      </span>
                      <span className={`grid place-items-center w-5 h-5 rounded-full border ${active ? "bg-cyan-brand border-cyan-brand text-ink-bg" : "border-white/25"}`}>
                        {active && <Check size={12} strokeWidth={3} />}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-8 space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">{t.pricing.hoursPerDay}</span>
                    <span className="font-display text-2xl text-cyan-brand font-bold" data-testid="calc-hours-value">{hoursPerDay} <span className="text-sm text-white/50">{t.pricing.hours}</span></span>
                  </div>
                  <Slider min={1} max={24} step={1} value={[hoursPerDay]} onValueChange={(v) => setConfig((c) => ({ ...c, hoursPerDay: v[0] }))} data-testid="calc-hours-slider" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">{t.pricing.daysPerWeek}</span>
                    <span className="font-display text-2xl text-cyan-brand font-bold" data-testid="calc-days-value">{daysPerWeek} <span className="text-sm text-white/50">{t.pricing.days}</span></span>
                  </div>
                  <Slider min={1} max={7} step={1} value={[daysPerWeek]} onValueChange={(v) => setConfig((c) => ({ ...c, daysPerWeek: v[0] }))} data-testid="calc-days-slider" />
                </div>
              </div>
            </div>
          </Reveal>

          {/* Estimate panel */}
          <Reveal delay={0.1}>
            <div className="lg:sticky lg:top-28 rounded-3xl border border-cyan-brand/25 bg-ink-surface/70 backdrop-blur-2xl p-7 md:p-9 glow-cyan">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-brand mb-4">{t.pricing.estimateLabel}</p>

              <div className="flex rounded-full border border-white/10 p-1 mb-6" data-testid="calc-billing-toggle">
                {["weekly", "monthly"].map((b) => (
                  <button
                    key={b}
                    onClick={() => setConfig((c) => ({ ...c, billing: b }))}
                    data-testid={`calc-billing-${b}`}
                    className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors duration-200 ${
                      billing === b ? "bg-white text-ink-bg" : "text-white/55 hover:text-white"
                    }`}
                  >
                    {t.pricing[b]}
                  </button>
                ))}
              </div>

              {apps.length === 0 ? (
                <p className="text-white/55 text-sm py-8" data-testid="calc-no-apps">{t.pricing.noApps}</p>
              ) : (
                <>
                  <div className="py-4">
                    <span className="block font-display text-6xl md:text-7xl font-extrabold text-white tracking-tighter" data-testid="calc-total">
                      {euro(displayCost)}
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/45 mt-2 block">
                      {billing === "weekly" ? t.pricing.weeklyCost : t.pricing.monthlyCost}
                    </span>
                  </div>

                  <div className="mt-4 border-t border-white/10 pt-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-3">{t.pricing.breakdown}</p>
                    <ul className="space-y-2">
                      {apps.map((id) => (
                        <li key={id} className="flex items-center justify-between text-sm">
                          <span className="text-white/70">{t.appNames[id]}</span>
                          <span className="font-mono text-white/50">{euro(appRate(id))}{t.pricing.perHour}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 flex items-center justify-between text-sm border-t border-white/10 pt-3">
                      <span className="text-white/70">{hoursPerDay} {t.pricing.hours} × {daysPerWeek} {t.pricing.days}</span>
                      <span className="font-mono text-white/50">{euro(cost.weekly)} / {t.pricing.weekly.toLowerCase()}</span>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl border border-cyan-brand/25 bg-cyan-brand/[0.07] p-4" data-testid="calc-savings">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-brand">{t.pricing.savingsLabel}</span>
                      <span className="font-display text-2xl font-extrabold text-cyan-brand tabular-nums" data-testid="calc-savings-value">{euro(savings.monthlySavings)}</span>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-sm">
                      <span className="text-white/60">{t.pricing.monthlyHoursLabel}</span>
                      <span className="font-mono text-white/70">{savings.monthlyHours} {t.pricing.hours}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1 text-sm">
                      <span className="text-white/60">{t.pricing.netLabel}</span>
                      <span className={`font-mono font-semibold ${netBenefit >= 0 ? "text-emerald-400" : "text-coral-soft"}`} data-testid="calc-net-value">{euro(netBenefit)}</span>
                    </div>
                    <p className="mt-3 text-white/45 text-xs leading-snug">{t.pricing.savingsHint}</p>
                  </div>

                  <button
                    onClick={() => onRequest({ ...config, estimate: displayCost })}
                    data-testid="calc-request-btn"
                    className="mt-6 w-full rounded-full bg-coral-brand text-white font-semibold py-3.5 hover:bg-coral-soft transition-colors duration-200 active:scale-95 glow-coral"
                  >
                    {t.pricing.requestCta}
                  </button>
                </>
              )}
            </div>
          </Reveal>
        </div>

        {/* Prebuilt plans */}
        <Reveal>
          <div className="mt-24 max-w-2xl">
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white">{t.pricing.plansTitle}</h3>
            <p className="mt-3 text-white/60">{t.pricing.plansSub}</p>
          </div>
        </Reveal>

        <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          {PLANS.map((plan, i) => {
            const c = computeCost(plan.apps, 10, 6);
            return (
              <Reveal key={plan.id} delay={i * 0.06}>
                <div
                  data-testid={`plan-${plan.id}`}
                  className={`relative rounded-3xl border p-7 h-full flex flex-col transition-colors duration-300 ${
                    plan.popular ? "border-coral-brand/40 bg-coral-brand/[0.06]" : "border-white/10 bg-ink-surface/50 hover:border-white/25"
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute -top-3 left-7 font-mono text-[10px] uppercase tracking-[0.2em] bg-coral-brand text-white px-3 py-1 rounded-full">
                      {t.pricing.mostPopular}
                    </span>
                  )}
                  <h4 className="font-display text-2xl font-bold text-white">{t.plans[plan.id].name}</h4>
                  <p className="mt-2 text-white/55 text-sm leading-snug min-h-[40px]">{t.plans[plan.id].desc}</p>
                  <div className="mt-5">
                    <span className="font-display text-4xl font-extrabold text-white tracking-tight">{euro(c.monthly)}</span>
                    <span className="text-white/45 text-sm font-mono ml-1">/{t.pricing.monthly.toLowerCase()}*</span>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35 mt-4 mb-3">{t.pricing.includes}</p>
                  <ul className="space-y-2 flex-1">
                    {plan.apps.map((id) => (
                      <li key={id} className="flex items-center gap-2 text-sm text-white/75">
                        <Check size={14} className="text-cyan-brand shrink-0" /> {t.appNames[id]}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => applyPlan(plan.apps)}
                    data-testid={`plan-choose-${plan.id}`}
                    className={`mt-6 w-full rounded-full py-3 font-semibold text-sm transition-colors duration-200 active:scale-95 ${
                      plan.popular ? "bg-coral-brand text-white hover:bg-coral-soft" : "bg-white/10 text-white hover:bg-white hover:text-ink-bg"
                    }`}
                  >
                    {t.pricing.choosePlan}
                  </button>
                  <p className="mt-3 font-mono text-[10px] text-white/30">* 10 {t.pricing.hours} × 6 {t.pricing.days}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
